/**
 * <brand-quiz> — a short routine finder.
 *
 * Progressive enhancement: the markup renders every question and every result as plain
 * content. Only once this module runs does it add `quiz--enhanced`, which is what the CSS
 * uses to hide inactive steps. With JavaScript off the page is still readable and every
 * product link still works — it just shows all the questions at once.
 *
 * Scoring: each answer carries a result key. The key with the most answers wins; ties fall
 * to whichever result the merchant ordered first, so the outcome is always deterministic.
 */
class BrandQuiz extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.results = Array.from(this.querySelectorAll('[data-quiz-result]'));
    this.progress = this.querySelector('[data-quiz-progress]');
    this.count = this.querySelector('[data-quiz-count]');
    this.bar = this.querySelector('[data-quiz-bar]');
    this.status = this.querySelector('[data-quiz-status]');

    if (!this.steps.length || !this.results.length) return;

    this.classList.add('quiz--enhanced');
    this.#reset();

    const { signal } = this.#abortController;

    this.addEventListener('click', this.#onClick, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  #onClick = (event) => {
    const answer = event.target.closest('[data-quiz-answer]');
    if (answer) {
      event.preventDefault();
      this.#answer(answer.dataset.quizAnswer);
      return;
    }

    if (event.target.closest('[data-quiz-back]')) {
      event.preventDefault();
      this.#back();
      return;
    }

    if (event.target.closest('[data-quiz-restart]')) {
      event.preventDefault();
      this.#reset();
    }
  };

  #reset() {
    this.answers = [];
    this.index = 0;
    this.#render();
  }

  /** @param {string} key */
  #answer(key) {
    this.answers[this.index] = key;
    this.index += 1;
    this.#render();
  }

  #back() {
    if (this.index === 0) return;
    this.index -= 1;
    this.answers.length = this.index;
    this.#render();
  }

  #render() {
    const finished = this.index >= this.steps.length;

    this.steps.forEach((step, i) => {
      step.hidden = finished || i !== this.index;
    });

    const winner = finished ? this.#winner() : null;

    this.results.forEach((result) => {
      result.hidden = !finished || result.dataset.quizResult !== winner;
    });

    if (this.progress) this.progress.hidden = finished;

    if (this.count) {
      this.count.textContent = `Question ${Math.min(this.index + 1, this.steps.length)} of ${this.steps.length}`;
    }

    if (this.bar) {
      // Fill reflects questions answered, so the bar starts empty and completes on the last answer.
      this.bar.style.inlineSize = `${(this.index / this.steps.length) * 100}%`;
    }

    if (this.status && finished) {
      const heading = this.querySelector(`[data-quiz-result="${winner}"] [data-quiz-result-title]`);
      this.status.textContent = heading ? `Your match: ${heading.textContent.trim()}` : 'Your match is ready.';
    } else if (this.status) {
      this.status.textContent = '';
    }

    // Move focus to whatever is now on screen, so keyboard and screen reader users follow along.
    const active = finished
      ? this.querySelector(`[data-quiz-result="${winner}"]`)
      : this.steps[this.index];
    active?.focus({ preventScroll: true });
  }

  /** @returns {string} the result key with the most answers */
  #winner() {
    const tally = new Map();
    for (const key of this.answers) {
      tally.set(key, (tally.get(key) || 0) + 1);
    }

    let best = this.results[0]?.dataset.quizResult;
    let bestCount = -1;

    // Iterate results in author order so ties resolve predictably.
    for (const result of this.results) {
      const key = result.dataset.quizResult;
      const count = tally.get(key) || 0;
      if (count > bestCount) {
        best = key;
        bestCount = count;
      }
    }

    return best;
  }
}

if (!customElements.get('brand-quiz')) {
  customElements.define('brand-quiz', BrandQuiz);
}
